import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import { Formik } from 'formik';
import { signUpWithEmail } from '../../service/auth';
import Iconify from '../iconify';
import CircularProgress from '@mui/material/CircularProgress';
import * as Yup from 'yup';
import { passwordRules, confirmPasswordRules } from '../../service/passwordValidation';

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required('Required').min(2, 'Min 2 chars'),
  lastName: Yup.string().required('Required').min(2, 'Min 2 chars'),
  email: Yup.string().email('Invalid email').required('Required'),
  password: passwordRules,
  confirmPassword: confirmPasswordRules,
});

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: 'error' };

  let score = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };

  Object.values(checks).forEach((passed) => {
    if (passed) score += 20;
  });

  if (score <= 40) {
    return { score, label: 'Weak', color: 'error' };
  } else if (score <= 60) {
    return { score, label: 'Fair', color: 'warning' };
  } else if (score <= 80) {
    return { score, label: 'Good', color: 'info' };
  } else {
    return { score, label: 'Strong', color: 'success' };
  }
};

export default function EmailSignUpModal({
  open,
  handleClose,
  onAccountCreated,
  requirePhoneVerification = true,
  onSignInInstead,
  onResetPassword,
}) {
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const firstInputRef = React.useRef(null);

  // Set when Firebase rejects the address as already registered. Holds the
  // address so the recovery buttons can carry it into sign-in or reset.
  const [takenEmail, setTakenEmail] = React.useState('');

  // Auto-focus first input when modal opens
  React.useEffect(() => {
    if (open) {
      setTakenEmail('');
      if (firstInputRef.current) {
        setTimeout(() => firstInputRef.current?.focus(), 100);
      }
    }
  }, [open]);

  const onFormSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setTakenEmail('');

    const displayName = `${values.firstName} ${values.lastName}`;
    const result = await signUpWithEmail(values.email, values.password, displayName);

    if (result && result.user) {
      onAccountCreated(result.user);
      handleClose();
      resetForm();
      setLoading(false);
      return;
    }

    // signUpWithEmail has already toasted the reason. For an address that
    // is already registered — the likeliest failure now that these are real
    // accounts — also offer a way through rather than a dead end.
    if (result && result.error === 'auth/email-already-in-use') {
      setTakenEmail(values.email);
    }

    setLoading(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="signup-title"
      aria-describedby="signup-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="signup-title"
        data-testid="signup-modal"
        sx={{
          width: 520,
          bgcolor: '#fff',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <IconButton
            onClick={handleClose}
            aria-label="Close dialog"
            data-testid="signup-close"
            sx={{
              width: 36,
              minWidth: 36,
              height: 36,
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              color: '#374151',
              bgcolor: '#fff',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#F9FAFB' },
            }}
          >
            <Iconify icon="eva:arrow-back-fill" width={16} sx={{ color: '#374151' }} />
          </IconButton>
        </Box>

        {/* Title */}
        <Box sx={{ textAlign: 'center', pb: 2.5 }}>
          <Typography
            id="signup-title"
            sx={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}
          >
            Create Account
          </Typography>
          <Typography
            id="signup-description"
            sx={{ fontSize: '14px', color: '#6B7280', mt: 0.5 }}
          >
            Create an account to continue
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              email: '',
              password: '',
              confirmPassword: '',
            }}
            validationSchema={SignupSchema}
            onSubmit={onFormSubmit}
          >
            {({ handleChange, handleSubmit, touched, errors, values }) => {
              const passwordStrength = calculatePasswordStrength(values.password);

              return (
                <form onSubmit={handleSubmit} noValidate style={{ width: '100%', minWidth: 0, padding: 0, margin: 0, boxShadow: 'none' }}>
                  {/* Name Row */}
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}
                      >
                        First Name
                      </Typography>
                      <TextField
                        inputRef={firstInputRef}
                        name="firstName"
                        placeholder="John"
                        value={values.firstName}
                        onChange={handleChange}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                        aria-label="First name"
                        inputProps={{ 'data-testid': 'signup-firstname' }}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 42,
                            borderRadius: '12px',
                            fontSize: '14px',
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                            '&.Mui-focused fieldset': { borderColor: '#242258', borderWidth: 1 },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}
                      >
                        Last Name
                      </Typography>
                      <TextField
                        name="lastName"
                        placeholder="Doe"
                        value={values.lastName}
                        onChange={handleChange}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        aria-label="Last name"
                        inputProps={{ 'data-testid': 'signup-lastname' }}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 42,
                            borderRadius: '12px',
                            fontSize: '14px',
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                            '&.Mui-focused fieldset': { borderColor: '#242258', borderWidth: 1 },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Email */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      name="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      aria-label="Email address"
                      autoComplete="email"
                      inputProps={{ 'data-testid': 'signup-email' }}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 42,
                          borderRadius: '12px',
                          fontSize: '14px',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover fieldset': { borderColor: '#D1D5DB' },
                          '&.Mui-focused fieldset': { borderColor: '#242258', borderWidth: 1 },
                        },
                      }}
                    />
                  </Box>

                  {/* Password */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}
                    >
                      Password
                    </Typography>
                    <TextField
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={values.password}
                      onChange={handleChange}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      aria-label="Password"
                      autoComplete="new-password"
                      inputProps={{ 'data-testid': 'signup-password' }}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 42,
                          borderRadius: '12px',
                          fontSize: '14px',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover fieldset': { borderColor: '#D1D5DB' },
                          '&.Mui-focused fieldset': { borderColor: '#242258', borderWidth: 1 },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              <Iconify
                                icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                                width={16}
                                sx={{ color: '#9CA3AF' }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Password Strength Meter */}
                    {values.password && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={passwordStrength.score}
                          color={passwordStrength.color}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#E5E7EB',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 500,
                              color:
                                passwordStrength.color === 'error'
                                  ? '#DC2626'
                                  : passwordStrength.color === 'warning'
                                  ? '#D97706'
                                  : passwordStrength.color === 'info'
                                  ? '#2563EB'
                                  : '#10B981',
                            }}
                          >
                            {passwordStrength.label}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                            {passwordStrength.score}%
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Confirm Password */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}
                    >
                      Confirm Password
                    </Typography>
                    <TextField
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      aria-label="Confirm password"
                      autoComplete="new-password"
                      inputProps={{ 'data-testid': 'signup-confirm-password' }}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 42,
                          borderRadius: '12px',
                          fontSize: '14px',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover fieldset': { borderColor: '#D1D5DB' },
                          '&.Mui-focused fieldset': { borderColor: '#242258', borderWidth: 1 },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                              aria-label={
                                showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'
                              }
                            >
                              <Iconify
                                icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                                width={16}
                                sx={{ color: '#9CA3AF' }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Address already registered: offer both ways through */}
                  {takenEmail && (
                    <Box
                      role="alert"
                      aria-live="assertive"
                      sx={{ py: 1.5, px: 2, mb: 2, bgcolor: '#FEF2F2', borderRadius: '12px' }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#991B1B', mb: 1 }}>
                        An account already exists for {takenEmail}.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          type="button"
                          onClick={() => onSignInInstead(takenEmail)}
                          data-testid="signup-signin-instead"
                          sx={{
                            flex: 1,
                            height: 38,
                            bgcolor: '#242258',
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#1a1a45', boxShadow: 'none' },
                          }}
                        >
                          Sign in instead
                        </Button>
                        <Button
                          type="button"
                          onClick={() => onResetPassword(takenEmail)}
                          data-testid="signup-reset-password"
                          sx={{
                            flex: 1,
                            height: 38,
                            color: '#242258',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            textTransform: 'none',
                            border: '1px solid #E5E7EB',
                            '&:hover': { bgcolor: '#F9FAFB' },
                          }}
                        >
                          Reset password
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Info Box */}
                  {requirePhoneVerification && (
                    <Box
                      role="alert"
                      aria-live="polite"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75,
                        py: 1.25,
                        mb: 2,
                        bgcolor: '#EFF6FF',
                        borderRadius: '12px',
                      }}
                    >
                      <Iconify
                        icon="eva:info-outline"
                        width={16}
                        sx={{ color: '#2563EB' }}
                        aria-hidden="true"
                      />
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#2563EB' }}>
                        Phone verification required next
                      </Typography>
                    </Box>
                  )}

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={loading}
                    fullWidth
                    data-testid="signup-submit"
                    sx={{
                      height: 46,
                      bgcolor: '#242258',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#1a1a45', boxShadow: 'none' },
                      '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                    }}
                  >
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create Account'}
                  </Button>

                  {/* Terms */}
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: '#9CA3AF',
                      textAlign: 'center',
                      mt: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    By creating an account, you agree to our{' '}
                    <Box
                      component="a"
                      href="/terms"
                      sx={{ color: '#242258', fontWeight: 600, textDecoration: 'none' }}
                    >
                      Terms
                    </Box>{' '}
                    and{' '}
                    <Box
                      component="a"
                      href="/privacy"
                      sx={{ color: '#242258', fontWeight: 600, textDecoration: 'none' }}
                    >
                      Privacy Policy
                    </Box>
                  </Typography>
                </form>
              );
            }}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
}
